import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class BackOfficeStorageService {
  private readonly prefix = "backoffice";

  private getPrefixedKey(key: string): string {
    return `${this.prefix}.${key}`;
  }

  set<T>(key: string, value: T): void {
    try {
      const storageKey = this.getPrefixedKey(key);
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(storageKey, serializedValue);
    } catch (error) {
      console.error("Error storing value in localStorage:", error);
    }
  }

  get<T>(key: string, defaultValue: T | null = null): T | null {
    try {
      const storageKey = this.getPrefixedKey(key);
      const value = localStorage.getItem(storageKey);

      if (value === null) {
        return defaultValue;
      }

      return JSON.parse(value) as T;
    } catch (error) {
      console.error("Error reading value from localStorage:", error);
      return defaultValue;
    }
  }

  remove(key: string): void {
    try {
      const storageKey = this.getPrefixedKey(key);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Error removing value from localStorage:", error);
    }
  }

  clearAll(): void {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${this.prefix}.`)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error("Error clearing backOffice localStorage keys:", error);
    }
  }

  multiSet(entries: Record<string, any>): void {
    try {
      Object.keys(entries).forEach((key) => {
        this.set(key, entries[key]);
      });
    } catch (error) {
      console.error("Error in multiSet:", error);
    }
  }

  multiGet<T = any>(keys: string[]): Record<string, T | null> {
    const result: Record<string, T | null> = {};

    try {
      keys.forEach((key) => {
        result[key] = this.get<T>(key);
      });
    } catch (error) {
      console.error("Error in multiGet:", error);
    }

    return result;
  }

  has(key: string): boolean {
    try {
      const storageKey = this.getPrefixedKey(key);
      return localStorage.getItem(storageKey) !== null;
    } catch (error) {
      console.error("Error checking key in localStorage:", error);
      return false;
    }
  }

  keys(): string[] {
    const result: string[] = [];

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (key && key.startsWith(`${this.prefix}.`)) {
          result.push(key.replace(`${this.prefix}.`, ""));
        }
      }
    } catch (error) {
      console.error("Error getting backOffice localStorage keys:", error);
    }

    return result;
  }
}
